import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { usePortalDashboard } from '@/hooks/use-portal'
import { useAuthStore } from '@/stores/auth.store'
import { ArrowRight, Loader2, Upload, UserCheck, Users } from 'lucide-react'
import { Link } from 'react-router-dom'

export function PortalDashboardPage() {
  const { data: stats, isLoading } = usePortalDashboard()
  const { user } = useAuthStore()

  const firstName = user?.first_name || user?.full_name?.split(' ')[0]

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const totalClients = stats?.total_employees ?? 0
  const activeClients = stats?.active_employees ?? 0
  const totalImports = stats?.total_imports ?? 0

  return (
    <div className="p-8 space-y-8 max-w-5xl">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {firstName ? `Welcome back, ${firstName}` : 'Dashboard'}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Overview of your portal</p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total People</p>
                <p className="text-3xl font-semibold mt-1">{totalClients}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active</p>
                <p className="text-3xl font-semibold mt-1">{activeClients}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-green-50 flex items-center justify-center">
                <UserCheck className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Imports</p>
                <p className="text-3xl font-semibold mt-1">{totalImports}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-purple-50 flex items-center justify-center">
                <Upload className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-3">
        <Button asChild>
          <Link to="/import">
            <Upload className="h-4 w-4 mr-2" />
            Import People
          </Link>
        </Button>
        <Button variant="outline" asChild>
          <Link to="/people">
            View All People
            <ArrowRight className="h-4 w-4 ml-2" />
          </Link>
        </Button>
      </div>

      {/* Recent Imports */}
      {stats?.recent_imports && stats.recent_imports.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent Imports</CardTitle>
            <CardDescription>Last {stats.recent_imports.length} imports</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="h-10 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      File
                    </th>
                    <th className="h-10 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="h-10 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Success
                    </th>
                    <th className="h-10 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Failed
                    </th>
                    <th className="h-10 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      By
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_imports.map((imp) => (
                    <tr key={imp.id} className="border-b last:border-0">
                      <td className="p-4 text-sm font-medium">{imp.original_filename}</td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {new Date(imp.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-4 text-sm text-green-600 font-medium">
                        {imp.success_count}
                      </td>
                      <td className="p-4 text-sm text-red-600 font-medium">{imp.failure_count}</td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {imp.uploaded_by_name || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
